/**
 * useInsertBlockDialog — Track 3 Phase 2
 *
 * Owns the INSERT command's block-placement dialog feature extracted
 * from `App.tsx` as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Mirrors `draftSession.step === "insert-dialog"` into a local open
 *     flag (auto-open driven by the draft state machine — closes on
 *     other INSERT steps and on null draft session).
 *   - Owns the Confirm / Cancel handlers. Confirm runs the cyclic
 *     reference / explodable checks, threads ATTDEF prompt-tags into
 *     the draft session for the on-screen flow, or — for the immediate
 *     case — runs the overlap interceptor and dispatches if no overlap
 *     was detected.
 *   - Exposes `close()` so legacy resetShellState() still has a kill
 *     switch (matches the Phase 1 pattern).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: {
 *       onConfirm,
 *       onCancel,
 *       blocks,
 *       blockRecords,
 *       initialBlockName,
 *     },
 *     close,
 *   }
 *
 * The orchestrator spreads `props` onto `<InsertBlockDialog>` and calls
 * `close()` from `resetShellState()`.
 *
 * AutoCAD parity:
 *   AutoCAD's INSERT command opens the "Insert" dialog with block
 *   selection, scale / rotation specifiers, and the "Specify on screen"
 *   checkboxes. The hook only owns orchestration — the dialog component
 *   itself preserves the full AutoCAD-parity surface.
 */

import { useCallback, useEffect, useState } from "react";

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import type {
  BlockDefinition,
  BlockRecord,
} from "@dxf-ai-editor/drawing-model";

import type { InsertBlockParams } from "../components/InsertBlockDialog";
import { useEditorStore } from "../state/editorStore";
import { useT } from "../i18n";

import type { DraftSession } from "../scene/draftingHelpers";

export interface UseInsertBlockDialogParams {
  /** Current draft session — auto-opens dialog when step === "insert-dialog". */
  draftSession: DraftSession | null;
  /** Cleared by Confirm / Cancel handlers (and by the on-screen path). */
  setDraftSession: (session: DraftSession | null) => void;
  /** Cleared by Confirm / Cancel handlers. */
  setCommandText: (text: string) => void;
  /** Receives localized success / error feed entries. */
  appendCommandFeed: (text: string) => void;
  /**
   * Overlap interceptor (from `useOverlapWarningDialog`). Returns `true`
   * when the dispatched command was stashed for user confirmation; the
   * confirm handler defers dispatch in that case.
   */
  interceptOverlapForBlockInsert: (command: EditorCommand) => boolean;
}

export interface UseInsertBlockDialogResult {
  /** Whether to mount `<InsertBlockDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<InsertBlockDialog>`. */
  props: {
    onConfirm: (params: InsertBlockParams) => void;
    onCancel: () => void;
    blocks: Record<string, BlockDefinition>;
    blockRecords: Record<string, BlockRecord>;
    initialBlockName?: string;
  };
  /** Imperative close — used by `resetShellState()`. */
  close: () => void;
}

export function useInsertBlockDialog({
  draftSession,
  setDraftSession,
  setCommandText,
  appendCommandFeed,
  interceptOverlapForBlockInsert,
}: UseInsertBlockDialogParams): UseInsertBlockDialogResult {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);
  const activeSpaceId = useEditorStore(
    (store) => store.state.drawing.activeSpaceId,
  );

  const [isOpen, setIsOpen] = useState(false);

  // Auto-open driven by the draft state machine. Mirrors the original
  // multi-clause logic from App.tsx: open on `insert-dialog`, close on
  // any other INSERT step, close on null draft session.
  useEffect(() => {
    if (draftSession?.step === "insert-dialog") {
      setIsOpen(true);
    } else if (draftSession?.command === "INSERT") {
      setIsOpen(false);
    }
    if (!draftSession) {
      setIsOpen(false);
    }
  }, [draftSession]);

  const onConfirm = useCallback(
    (params: InsertBlockParams) => {
      const selected = geometryDocument.blocks?.[params.blockName];
      if (!selected) {
        appendCommandFeed(t("insert_dialog.error.no_blocks"));
        setIsOpen(false);
        setDraftSession(null);
        return;
      }
      // Cyclic reference check: if the active space is a block-edit
      // session for this exact block name (Phase 2 BEDIT), or if the
      // selected block's own entities reference this name transitively,
      // reject. Phase 1 only checks immediate containment.
      const blockDef = selected;
      for (const child of blockDef.entities) {
        if (child.type === "Block" && child.blockName === params.blockName) {
          appendCommandFeed(
            t("toast.block.cyclic_reference", { name: params.blockName }),
          );
          setIsOpen(false);
          setDraftSession(null);
          return;
        }
      }
      // If the user asked for explode-on-insert on a non-explodable
      // block, tell them and refuse — the InsertBlockDialog already
      // disables the checkbox but a command-line INSERT can still
      // bypass the dialog.
      const blockRecord = geometryDocument.blockRecords?.[params.blockName];
      if (params.explode && blockRecord?.explodable === false) {
        appendCommandFeed(
          t("toast.block.not_explodable", { name: params.blockName }),
        );
        setIsOpen(false);
        setDraftSession(null);
        return;
      }
      // Collect ATTDEFs that require user prompts (i.e. no
      // constant/preset flag) and queue them for the shell flow.
      const promptTags: Array<{ tag: string; defaultValue: string }> = [];
      for (const child of blockDef.entities) {
        if (child.type === "Attdef") {
          const flags = child.flags ?? {};
          if (!flags.constant && !flags.preset) {
            promptTags.push({
              tag: child.tag,
              defaultValue: child.defaultValue ?? "",
            });
          }
        }
      }
      if (params.specifyInsertionOnScreen && draftSession) {
        const updated = {
          ...draftSession,
          step: "insert-specify-point" as const,
          options: {
            ...draftSession.options,
            blockName: params.blockName,
            specifyScaleOnScreen: params.specifyScaleOnScreen
              ? "true"
              : "false",
            specifyRotationOnScreen: params.specifyRotationOnScreen
              ? "true"
              : "false",
            pendingAttribTags:
              promptTags.length > 1
                ? JSON.stringify(promptTags.slice(1).map((p) => p.tag))
                : "",
            attribTag: promptTags[0]?.tag ?? "",
            attribDefault: promptTags[0]?.defaultValue ?? "",
            uniformScale: params.uniformScale ? "true" : "false",
            scaleX: String(params.scale.x),
            scaleY: String(params.scale.y),
            scaleZ: String(params.scale.z),
            rotation: String(params.rotation),
            explode: params.explode ? "true" : "false",
          },
        };
        for (const pt of promptTags) {
          (updated.options as Record<string, string>)[
            `attribDefault_${pt.tag}`
          ] = pt.defaultValue;
        }
        setDraftSession(updated);
        setIsOpen(false);
        return;
      }
      // Immediate insert (all parameters from dialog). Run the P7
      // overlap interceptor first — if it consumes the command, the
      // overlap dialog is responsible for completing or discarding it.
      const insertCommand: EditorCommand = {
        type: "geometry/create-block-insert",
        source: "toolbar",
        spaceId: activeSpaceId,
        blockName: params.blockName,
        insertionPoint: {
          x: params.insertionPoint.x,
          y: params.insertionPoint.y,
        },
        scaleX: params.scale.x,
        scaleY: params.uniformScale ? params.scale.x : params.scale.y,
        rotation: params.rotation,
      };
      setIsOpen(false);
      if (interceptOverlapForBlockInsert(insertCommand)) {
        // The overlap dialog now owns the pending insert. Keep the
        // draftSession in place — the Continue handler will clear it.
        return;
      }
      dispatch(insertCommand);
      appendCommandFeed(
        t("summary.block_inserted", { name: params.blockName }),
      );
      setDraftSession(null);
      setCommandText("");
    },
    [
      geometryDocument,
      activeSpaceId,
      draftSession,
      dispatch,
      appendCommandFeed,
      t,
      setDraftSession,
      setCommandText,
      interceptOverlapForBlockInsert,
    ],
  );

  const onCancel = useCallback(() => {
    setIsOpen(false);
    setDraftSession(null);
    setCommandText("");
  }, [setDraftSession, setCommandText]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const initialBlockName = draftSession?.options?.blockName;
  return {
    isOpen,
    props: {
      onConfirm,
      onCancel,
      blocks: geometryDocument.blocks ?? {},
      blockRecords: geometryDocument.blockRecords ?? {},
      // Spread `initialBlockName` only when defined — keeps the prop
      // omitted under `exactOptionalPropertyTypes`.
      ...(initialBlockName !== undefined ? { initialBlockName } : {}),
    },
    close,
  };
}
